/**
 * Notification Event Definition models - aligned with backend Alaric.Services.Messaging
 *
 * Event definitions are merchant-scoped and describe the types
 * of notifications that can be sent (e.g., "payment_received", "login_alert")
 * along with their allowed channels and template variables.
 */

import { NotificationChannel, NotificationPriority, SmsMessageType } from "./NotificationEnums";

/**
 * Response DTO for event definition list/detail.
 * Maps to: Alaric.Services.Messaging.Dto.EventDefinitionDto
 */
export interface EventDefinitionDto {
    /** Unique event definition identifier (GUID) */
    id: string;

    /** The merchant ID this event definition belongs to */
    merchantId: number;

    /** Unique event code identifier (e.g., "PAYMENT_RECEIVED", "OTP_CODE") */
    eventCode: string;

    /** Human-readable description of when this event is triggered */
    description?: string;

    /**
     * Channels allowed for this event as enum array.
     * Backend returns as array of NotificationChannel enum values.
     */
    allowedChannels: NotificationChannel[];

    /** List of allowed placeholder variable names (e.g., ["amount", "currency", "recipientName"]) */
    allowedVariables?: string[];

    /** List of allowed data payload keys for push notification pass-through */
    allowedDataPayloadKeys?: string[];

    /** Priority level for push notifications sent for this event */
    priority: NotificationPriority;

    /** SMS message type for this event (OTP vs transactional) */
    smsMessageType: SmsMessageType;

    /** Whether this event definition is active and can be used for new templates */
    isActive: boolean;
}

/**
 * Request DTO for creating a new event definition.
 * Maps to: Alaric.Services.Messaging.Dto.CreateEventDefinitionRequest
 */
export interface CreateEventDefinitionRequest {
    /** The merchant ID this event definition belongs to */
    merchantId: number;

    /** Unique event code identifier (max 100 chars, immutable after creation) */
    eventCode: string;

    /** Human-readable description (max 500 chars) */
    description?: string;

    /** Channels allowed for this event (required, at least one) */
    allowedChannels: NotificationChannel[];

    /** List of allowed placeholder variable names */
    allowedVariables?: string[];

    /** List of allowed data payload keys for push notification pass-through */
    allowedDataPayloadKeys?: string[];

    /** Priority level for push notifications (default: Normal) */
    priority?: NotificationPriority;

    /** SMS message type for this event (default: Otp) */
    smsMessageType?: SmsMessageType;

    /** Whether this event definition is active (default: true) */
    isActive?: boolean;
}

/**
 * Request DTO for updating an event definition.
 * Maps to: Alaric.Services.Messaging.Dto.UpdateEventDefinitionRequest
 */
export interface UpdateEventDefinitionRequest {
    /** Human-readable description (max 500 chars) */
    description?: string;

    /** Channels allowed for this event as enum array */
    allowedChannels?: NotificationChannel[];

    /** List of allowed placeholder variable names */
    allowedVariables?: string[];

    /** List of allowed data payload keys for push notification pass-through */
    allowedDataPayloadKeys?: string[];

    /** Priority level for push notifications */
    priority?: NotificationPriority;

    /** SMS message type for this event */
    smsMessageType?: SmsMessageType;

    /** Whether this event definition is active */
    isActive?: boolean;
}

// ============================================================================
// Helper functions for working with event definitions
// ============================================================================

/**
 * Parse allowed channels - handles both array (new format) and string (legacy format)
 */
export const parseAllowedChannels = (channels: NotificationChannel[] | string | null | undefined): NotificationChannel[] => {
    if (!channels) return [];

    // If already an array, return as-is (ensure they're valid enum values)
    if (Array.isArray(channels)) {
        return channels.filter((ch) => Object.values(NotificationChannel).includes(ch));
    }

    // Legacy string format support
    if (typeof channels === "string") {
        const channelMap: Record<string, NotificationChannel> = {
            email: NotificationChannel.Email,
            sms: NotificationChannel.Sms,
            push: NotificationChannel.Push,
            pull: NotificationChannel.Pull,
        };

        return channels
            .split(",")
            .map((ch) => ch.trim().toLowerCase())
            .filter((ch) => ch in channelMap)
            .map((ch) => channelMap[ch]);
    }

    return [];
};

/**
 * Format channels array (now just returns the array as-is for API requests)
 */
export const formatAllowedChannels = (channels: NotificationChannel[]): NotificationChannel[] => {
    return channels;
};

/**
 * Get default allowed channels array
 */
export const getDefaultAllowedChannels = (): NotificationChannel[] => [
    NotificationChannel.Email,
    NotificationChannel.Sms,
    NotificationChannel.Push,
    NotificationChannel.Pull,
];

/**
 * Create an empty event definition form object
 * @param merchantId The merchant ID for the event definition
 */
export const createEmptyEventDefinition = (merchantId: number): CreateEventDefinitionRequest => ({
    merchantId,
    eventCode: "",
    description: "",
    allowedChannels: getDefaultAllowedChannels(),
    allowedVariables: [],
    allowedDataPayloadKeys: [],
    priority: NotificationPriority.Normal,
    smsMessageType: SmsMessageType.Otp,
    isActive: true,
});
